import { useEffect, useState } from 'react'

import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  RolesAndLevelsEntitySelectorDocument,
  RolesAndLevelsEntitySelectorQuery,
} from 'generated/resources/graphql'

import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  IJobsAndRatesFormValues,
  SelectedLevelItem,
} from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'

interface ParsedEntity {
  expanded: boolean
  id: string
  name: string
  totalCount?: number
  subItems?: Array<{
    subId: string
    subName: string
    subSearchKeys?: string[]
    totalCount: number
  }>
}

interface RoleEntity {
  id: string
  name: string
  expanded: boolean
  totalCount: number
  levels: Array<LevelEntity>
}

interface LevelEntity {
  id: string
  name: string
  searchKeys: string[]
  totalCount: number
}

const getRoleEntities = (expandedElements: string[], data?: RolesAndLevelsEntitySelectorQuery) => {
  const { jobCatalog: jobCatalogData } = data || {}

  const levels =
    jobCatalogData?.treeLevelsConnection?.nodes || jobCatalogData?.levelsConnection?.nodes || []

  const groupedByRole = levels.reduce<
    Record<
      string,
      Array<{
        id: string
        name: string
        roleName: string
        searchKeys: string[]
        totalCount: number
      }>
    >
  >((acc, level) => {
    const name = level.name || ''
    const roleName = 'roleName' in level ? level.roleName : level?.ancestor?.name || ''
    const roleId =
      ('roleId' in level ? level.roleId : 'ancestor' in level ? level?.ancestor?.id : '0') || '0'

    if (!acc[roleId]) acc[roleId] = []

    acc[roleId].push({
      id: level.id,
      name: name,
      roleName: roleName,
      searchKeys: [name, roleName],
      totalCount: level.referenceContractsConnection?.totalCount || 0,
    })

    return acc
  }, {})

  return Object.entries(groupedByRole).map(
    ([id, roleLevels]): RoleEntity => ({
      id: id,
      name: roleLevels[0]?.roleName || '',
      expanded: expandedElements.includes(id),
      totalCount: roleLevels.length,
      levels: roleLevels,
    })
  )
}

const getLevelEntities = (data?: RolesAndLevelsEntitySelectorQuery) => {
  const { jobCatalog: jobCatalogData } = data || {}

  const levels =
    jobCatalogData?.treeLevelsConnection?.nodes || jobCatalogData?.levelsConnection?.nodes || []

  return levels.map(
    (level): LevelEntity => ({
      id: level.id,
      name: level.name || '',
      searchKeys: [level.name || ''],
      totalCount: level.referenceContractsConnection?.totalCount || 0,
    })
  )
}

const RolesAndLevelsEntitySelector = ({
  selectedLevelIds,
  setValues,
  existingResourceJobLevelIds,
  isJobCatalogTreeEnabled,
  isSingleSelector = false,
}: {
  selectedLevelIds?: string[]
  setValues: (values: Partial<IJobsAndRatesFormValues>) => void
  existingResourceJobLevelIds: string[]
  isJobCatalogTreeEnabled: boolean
  isSingleSelector?: boolean
}) => {
  const [expandedElements, setExpandedElements] = useState<string[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedLevelIds ?? [])
  const existingResourceJobLevelIdStrings = existingResourceJobLevelIds
  const legacyJobCatalog = !isJobCatalogTreeEnabled

  const { data: treeData, loading } = useQuery(RolesAndLevelsEntitySelectorDocument, {
    variables: {
      legacyJobCatalog,
    },
  })

  useEffect(() => {
    setSelectedIds(selectedLevelIds ?? [])
  }, [selectedLevelIds])

  const rolesEntities = getRoleEntities(expandedElements, treeData)
  const levelsEntities = getLevelEntities(treeData)

  const filteredEntities = rolesEntities
    .map((roleEntity) => ({
      ...roleEntity,
      levels: roleEntity.levels?.filter(
        (level) => !existingResourceJobLevelIdStrings.includes(String(level.id))
      ),
    }))
    .filter((roleEntity) => roleEntity.levels && roleEntity.levels.length > 0)
    .map(
      (roleEntity): ParsedEntity => ({
        id: String(roleEntity.id),
        name: roleEntity.name,
        expanded: roleEntity.expanded,
        totalCount: roleEntity.totalCount,
        subItems: roleEntity.levels.map((level) => ({
          subId: level.id,
          subName: level.name,
          subSearchKeys: level.searchKeys,
          totalCount: level.totalCount,
        })),
      })
    )

  const onItemExpandedChange = (id: string, expanded: boolean) => {
    setExpandedElements(
      expanded ? [id].concat(expandedElements) : expandedElements.filter((el) => el !== id)
    )
  }

  const mapSelectedItemsToSelectEntities = (
    selectedLevelIdsToSelect: string[],
    entities: ParsedEntity[]
  ): EntitySelectEntity[] => {
    const selectedLevelIdSet = new Set(selectedLevelIdsToSelect)

    return entities
      .map((parsedEntity) => {
        const filteredSubItems =
          parsedEntity.subItems?.filter((subItem) =>
            selectedLevelIdSet.has(String(subItem.subId))
          ) || []

        return {
          ...parsedEntity,
          subItems: filteredSubItems,
        }
      })
      .filter((parsedEntity) => parsedEntity.subItems && parsedEntity.subItems.length > 0)
  }

  const selectedEntities: EntitySelectEntity[] = mapSelectedItemsToSelectEntities(
    selectedIds,
    filteredEntities
  )

  const onSelect = (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
    let newSelectedItems: SelectedLevelItem[] = []

    if (selection === null) {
      newSelectedItems = []
    } else if (Array.isArray(selection)) {
      const flatSelected = flatSelection(selection)

      newSelectedItems = flatSelected.map((subItem) => {
        const originalLevel = levelsEntities.find(
          (level) => String(level.id) === String(subItem.subId)
        )

        const roleEntity = rolesEntities.find((role) =>
          role.levels.some((level) => level.id === originalLevel?.id)
        )

        return {
          id: String(subItem.subId),
          name: subItem.subName,
          type: 'level',
          roleName: roleEntity?.name || '',
          totalCount: originalLevel?.totalCount || 0,
          salaryRanges: [],
        }
      })
    } else {
      if (selection.subItems && selection.subItems.length > 0) {
        const flatSelected = selection.subItems

        newSelectedItems = flatSelected.map((subItem) => {
          const originalLevel = levelsEntities.find(
            (level) => String(level.id) === String(subItem.subId)
          )

          const roleEntity = rolesEntities.find((role) =>
            role.levels.some((level) => level.id === originalLevel?.id)
          )

          return {
            id: String(subItem.subId),
            name: subItem.subName,
            type: 'level',
            roleName: roleEntity?.name || '',
            totalCount: originalLevel?.totalCount || 0,
            salaryRanges: [],
          }
        })
      } else {
        const matchingLevel = levelsEntities.find(
          (level) => String(level.id) === String(selection.id)
        )

        if (matchingLevel) {
          const roleEntity = rolesEntities.find((role) =>
            role.levels.some((level) => level.id === matchingLevel.id)
          )

          newSelectedItems = [
            {
              id: matchingLevel.id,
              name: matchingLevel.name,
              type: 'level',
              roleName: roleEntity?.name || '',
              totalCount: matchingLevel.totalCount,
              salaryRanges: [],
            },
          ]
        } else {
          const totalCount =
            'totalCount' in selection && typeof selection.totalCount === 'number'
              ? selection.totalCount
              : 0

          newSelectedItems = [
            {
              id: String(selection.id),
              name: selection.name,
              type: 'level',
              roleName: '',
              totalCount,
              salaryRanges: [],
            },
          ]
        }
      }
    }

    setSelectedIds(newSelectedItems.map((item) => item.id))
    setValues({ levels: newSelectedItems })
  }

  return (
    <EntitySelect
      label={i18n.t('employee_selector.select_employees')}
      hideLabel
      alwaysOpen
      clearLabel={i18n.t('common.clear')}
      entities={filteredEntities}
      groups={[]}
      hiddenAvatar
      loading={loading}
      notFoundSubtitle={i18n.t('employee_selector.not_found.subtitle')}
      notFoundTitle={i18n.t('employee_selector.not_found.title')}
      onItemExpandedChange={(id, expanded) => onItemExpandedChange(String(id), expanded)}
      onSelect={onSelect}
      selectedEntities={selectedEntities}
      searchPlaceholder={i18n.t('common.search')}
      selectAllLabel={i18n.t('common.select_all')}
      onGroupChange={() => {}}
      selectedGroup={''}
      selectedLabel={i18n.t('common.selected')}
      singleSelector={isSingleSelector}
      selectedItemsCopy={i18n.t('employee_selector.employees_selected')}
      placeholder={i18n.t('employee_selector.select_employees')}
      width={380}
    />
  )
}

export default RolesAndLevelsEntitySelector
